import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { HoldingsComponentDCR } from '@ddt-web/config';
import { InjectConfig, ModelsExperienceConfigKey, SharedConfigKey } from '@ddt-web/config-layer';
import { CellData, CellUtil, ComponentData, DateFormatter, TableData } from '@ddt-web/data-service';
import { DateUtil } from '@ddt-web/generic-utils';
import * as get from 'lodash/get';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { ScrollableHoldingsTableBreakPointME } from '../../config';
import { ScrollSyncService } from '../../services/scroll-sync.service';
import { HoldingsTableUtil } from '../../utils';
@Component({
	selector: 'hl-holdings-table-header',
	templateUrl: './holdings-table-header.component.html',
	styleUrls: ['./holdings-table-header.component.scss']
})

export class HoldingsTableHeaderComponent implements OnInit {
	@InjectConfig(SharedConfigKey.SHOULD_USE_DAS_PORTFOLIO_ID) private readonly shouldUseDasPortfolioId: boolean = true;
	@InjectConfig(ModelsExperienceConfigKey.DATE_FORMAT) private readonly dateFormat: string = DateUtil.MODELS_US_DATE_FORMAT;
	@InjectConfig(ModelsExperienceConfigKey.ENABLE_HOLDINGS_TABLE_HEADER_COLUMN_WIDTH) private readonly enableColumnWidth: boolean = true;
	@InjectConfig(ModelsExperienceConfigKey.EQUITIES_ASSET_CLASSES) private readonly equitiesAssetClasses: string[] =
		['Australian Equities', 'Developed Market Equities', 'Emerging Market Equities', 'Property & Infrastructure'];
	@InjectConfig(ModelsExperienceConfigKey.FIXED_INCOME_ASSET_CLASSES) private readonly fixedIncomeAssetClasses: string[] =
		['Australian Fixed Income', 'International Fixed Income', 'Global Fixed Income', 'Alternatives', 'Cash & Cash Alternatives', 'Cash'];
	@Input() public data: ComponentData;
	@Input() public dataTabId: string;
	@Input() public holdingsData: ComponentData;
	@Input() public holdingsDataTabId: string;
	@Input() public multiRowLeftHeader: boolean;
	@Input() public isPortfolioClickable: boolean = false;
	@Input() public familyAsOf: string;
	@Input() public modelIds: string[];
	@Input() public headerColumns = [];

	@Output() public onPortfolioClicked: EventEmitter<string | number> = new EventEmitter<string | number>();
	@Output() public selectedPortfolio: EventEmitter<string> = new EventEmitter<string>();

	@ViewChild('scrollableTable', { read: ElementRef }) public scrollableTable: ElementRef<any>;

	public tableData: TableData;
	public holdingsTableData: TableData;
	public holdingsComponentDCR = HoldingsComponentDCR;
	public numberOfPortfolios: number;
	public selectedModelPortfolioData: { [key: string]: any };
	public indexes = [1]; // 1 has been set by default to pick model names on row 1.
	public showLatestAllocation: boolean = false;
	public showTargetAllocation: boolean = false;
	public showCompositeFee: boolean = false;
	private fields: string[] = [];
	public latestAllocationEquitiesRow: number[] = [];
	public latestAllocationFixedIncomeRow: number[] = [];
	public dateFormatter = new DateFormatter(this.dateFormat);
	public scrollBreakpoint = ScrollableHoldingsTableBreakPointME;

	private scrollLeftSubscription: Subscription;

	constructor(private scrollSyncService: ScrollSyncService) { }

	public ngOnInit(): void {
		this.fields = this.headerColumns.map(column => column.field);
		this.showLatestAllocation = this.fields.includes('latestAllocation');
		this.showTargetAllocation = this.fields.includes('targetAllocation');
		this.showCompositeFee = this.fields.includes('compositeFee');
		const tab = this.data.getTabById(this.dataTabId);
		if (this.data.success && !get(tab, 'error')) {
			this.tableData = get(tab, 'tableData[0]', []);
			this.numberOfPortfolios = this.tableData['cells']?.length;
			this.createHeaderRows();
			this.setSelectedModelPortfolioData();
			if (this.modelIds.length === 1) {
				this.handleDropDownChange(this.modelIds[0]);
			}
			else {
				this.setSelectedModelPortfolioData();
			}
		}
	}

	public ngAfterViewChecked() {
		this.scrollLeftSubscription = this.scrollSyncService.scrollLeft$
			.pipe(take(1))
			.subscribe(position => {
				this.scrollableTable.nativeElement.scrollLeft = position;
			});
	}

	public ngOnDestroy() {
		this.scrollLeftSubscription.unsubscribe();
	}

	public onScroll(event: Event) {
		const scrollLeftPosition = (event.target as HTMLElement).scrollLeft;
		this.scrollSyncService.sendScrollLeftPosition(scrollLeftPosition);
	}

	public onClickPortfolio(cellRow: CellData[]): void {
		const portfolioId = CellUtil.getFormattedCellValueById(cellRow, this.shouldUseDasPortfolioId ? 'portfolioId' : 'id');
		this.onPortfolioClicked.emit(portfolioId);
	}

	public handleDropDownChange(portfolioId: string): void {
		if (this.showLatestAllocation) {
			this.latestAllocationEquitiesRow = [];
			this.latestAllocationFixedIncomeRow = [];
			this.createLatestAllocationRow();
		}
		this.selectedModelPortfolioData = this.tableData['rows'].filter(item => item.id?.formattedValue === portfolioId)
			.map(filteredItem => Object.assign({}, {
				expenseRatio: filteredItem.expenseRatio?.formattedValue,
				grossExpenseRatio: filteredItem.grossExpenseRatio?.formattedValue,
				name: filteredItem.name?.formattedValue,
				id: filteredItem.id?.formattedValue,
				latestAllocation: (this.latestAllocationEquitiesRow[0] || 0) + '/' + (this.latestAllocationFixedIncomeRow[0] || 0),
				targetAllocation: filteredItem.name?.formattedValue,
				compositeFee: filteredItem.expenseRatio?.formattedValue
			}
			)
			)[0];
	}

	public setPortfolioId(portfolioId: string): void {
		this.selectedPortfolio.emit(portfolioId);
	}

	@HostListener('window:resize', ['$event'])
	private onResize(): void {
		this.setSelectedModelPortfolioData();
	}

	private setSelectedModelPortfolioData() {
		this.selectedModelPortfolioData = {
			expenseRatio: this.tableData['rows'][0].expenseRatio?.formattedValue,
			grossExpenseRatio: this.tableData['rows'][0].grossExpenseRatio?.formattedValue,
			name: this.tableData['rows'][0].name?.formattedValue,
			id: this.tableData['rows'][0].id?.formattedValue,
			compositeFee: this.tableData['rows'][0].expenseRatio?.formattedValue,
			latestAllocation: (this.latestAllocationEquitiesRow[0] || 0) + '/' + (this.latestAllocationFixedIncomeRow[0] || 0),
			targetAllocation: this.tableData['rows'][0].name?.formattedValue
		};
	}

	private createHeaderRows(): void {
		this.indexes.push(
			...this.tableData.headersX
				.map((item, index) => {
					if (this.fields.includes(item.config.id)) {
						return index;
					}
				})
				.filter(item => !!item)
		);
		if (this.showLatestAllocation) {
			this.createLatestAllocationRow();
		}
	}

	private createLatestAllocationRow() {
		const holdingsTab = this.holdingsData.getTabById(this.holdingsDataTabId);
		if (this.holdingsData.success && !get(holdingsTab, 'error')) {
			this.holdingsTableData = holdingsTab?.tableData?.[0];
			const categoryIndexMap = HoldingsTableUtil.populateCategoryIndexMap(this.holdingsTableData);
			const categoryWeightMap = HoldingsTableUtil.populateCategoryWeightMap(this.holdingsTableData, categoryIndexMap);
			for (const [category, value] of categoryWeightMap) {
				if (this.equitiesAssetClasses.includes(category)) {
					if (this.latestAllocationEquitiesRow.length === 0) {
						this.latestAllocationEquitiesRow = value;
					} else {
						value.forEach((item, index) => {
							this.latestAllocationEquitiesRow[index] = parseFloat((+this.latestAllocationEquitiesRow[index] + +item).toFixed(1));
						});
					}
				}
				if (this.fixedIncomeAssetClasses.includes(category)) {
					if (this.latestAllocationFixedIncomeRow.length === 0) {
						this.latestAllocationFixedIncomeRow = value;
					} else {
						value.forEach((item, index) => {
							this.latestAllocationFixedIncomeRow[index] = parseFloat((+this.latestAllocationFixedIncomeRow[index] + +item).toFixed(1));
						});
					}
				}
			}
		}
	}
}
